import Grid from '@material-ui/core/Grid'
import React, { useCallback } from 'react'
import { Button } from '../../../../components/common/Buttons'
import { ModalDialog } from '../../../../components/common/Dialogs'
import { useTrans } from '../../../../services/i18n'
import { useAlertContext } from '../../../../contexts/AlertContext'
import { useForm } from '../../../../hooks/useForm'
import { Autocomplete } from '../../../../components/common/Inputs'
import * as Yup from 'yup'

const formOptions = {
  validateOnMount: false,
  validateOnChange: false,
  enableReinitialize: true,
}

// Schema validation
const useScheme = () => {
  const { trans } = useTrans()
  return Yup.object({
    user: Yup.object().required(trans('required')),
  })
}

// Initial values
const getInitialValues = (form) => ({
  user: form?.assignedUser || null,
})

export default function AssignUserStaffModal({
  form,
  open,
  onClose,
  assignFormToStaff,
  fetchList,
  users,
  batchUpdateStatus,
}) {
  const { trans } = useTrans()
  const schema = useScheme()
  const { successAlert, errorAlert } = useAlertContext()
  const [isSending, setIsSending] = React.useState(false)

  const closeModal = () => {
    resetForm()
    onClose()
  }

  const onSubmit = useCallback(
    async (values) => {
      if (!assignFormToStaff || !form) return

      setIsSending(true)
      try {
        const response = await assignFormToStaff({
          organizationFormId: form.id,
          userId: values.user.userName // userName is actually the userId in the users array
        })

        console.log('🔍 Assignment response:', response)

        // Check if there's an error in the response
        if (response.error) {
          console.error('❌ Error in response:', response.error)
          errorAlert(response.error.message || 'Failed to assign form')
          return
        }

        // Check if the payload indicates success
        if (!response.payload?.success) {
          console.error('❌ Assignment failed:', response.payload)
          errorAlert('Failed to assign form')
          return
        }

        console.log('✅ Assignment successful, updating status to progress')

        // Update admin status to 'progress' after successful assignment
        const statusResponse = await batchUpdateStatus({
          forms: [{
            organizationFormId: form.id,
            adminStatus: 'progress'
          }]
        })

        console.log('🔍 Status update response:', statusResponse)

        successAlert(response.payload?.message || 'Form assigned successfully')
        closeModal()
        fetchList()
      } catch (error) {
        console.error('💥 Exception during assignment:', error)
        errorAlert('Failed to assign form')
      } finally {
        setIsSending(false)
      }
    },
    [assignFormToStaff, form, closeModal, fetchList, errorAlert, successAlert, batchUpdateStatus]
  )

  const {
    getFieldProps,
    handleSubmit,
    values,
    isSubmitting,
    isValid,
    resetForm,
  } = useForm(schema, getInitialValues(form), onSubmit, formOptions)

  return (
    <ModalDialog
      open={open}
      onClose={closeModal}
      title={trans('assign-user-staff-to-form')}
      actions={
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              disabled={isSending}
              onClick={closeModal}
            >
              {trans('cancel')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              disabled={isSending || !isValid}
              pending={isSending}
              onClick={handleSubmit}
            >
              {trans('confirm')}
            </Button>
          </Grid>
        </Grid>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Autocomplete
            options={users || []}
            label={trans('select-user')}
            placeholder={trans('required')}
            {...getFieldProps('user')}
          />
        </Grid>
      </Grid>
    </ModalDialog>
  )
}